import React, { useState, useRef, useEffect } from "react";

const AstronautImage = ({
  backgroundSrc = "/assets/img/webp/bg_astro.webp",
  astronautSrc = "/assets/img/webp/iq_astro.webp",
  alt = "Astronaut",
  imageHeight = "h-[280px]",
  className = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExternalHover, setIsExternalHover] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [externalTarget, setExternalTarget] = useState(null); // "napiste", "zavolajte", or "vyplnte"
  const [blurEffect, setBlurEffect] = useState(false);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null); // New ref for resetting animation
  const currentRotation = useRef({
    x: 0,
    y: 0,
    z: 0,
    translateX: 0,
    translateY: 0,
  });
  const targetRotation = useRef({
    x: 0,
    y: 0,
    z: 0,
    translateX: 0,
    translateY: 0,
  });
  const prevExternalTarget = useRef(null);
  const wasHovered = useRef(false); // Track previous hover state

  // Animation constants
  const ANIMATION_SPEED = {
    default: 0.07, // Default animation speed
    externalHover: 0.035, // Slower speed for external hover targets (half speed)
    externalExit: 0.04, // Speed when exiting external hover
    mouseHover: 0.1, // Faster for direct mouse movement
  };
  const IDLE_MOVEMENT_AMPLITUDE = 1.8; // How much it moves in idle state

  // Reset function to reset animation state
  const resetAnimationState = () => {
    prevExternalTarget.current = null;

    // Don't completely reset rotation to avoid jumps,
    // but make sure it's not blocking external animations
    wasHovered.current = false;
  };

  // Handle mouse enter effect with longer blur and smoother transition
  const handleMouseEnter = () => {
    // Clear any reset timeout if it's pending
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    wasHovered.current = true;
    setIsHovered(true);
    setBlurEffect(true);

    // Clear any existing timeout to prevent issues
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Unblur after 300ms for a more noticeable effect
    blurTimeoutRef.current = setTimeout(() => {
      setBlurEffect(false);
    }, 300);
  };

  // Handle mouse movement for 3D rotation effect
  const handleMouseMove = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();

    // Calculate mouse position relative to the center of the container
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    setMousePosition({ x, y });
  };

  // Handle mouse leave with blur transition
  const handleMouseLeave = () => {
    // Apply blur briefly when leaving too
    setBlurEffect(true);

    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    // Remove blur and hover state after transition
    blurTimeoutRef.current = setTimeout(() => {
      setBlurEffect(false);
      setIsHovered(false);

      // Schedule a reset of the animation state after leaving
      // This is key to fixing the fast click-and-leave issue
      resetTimeoutRef.current = setTimeout(() => {
        resetAnimationState();
      }, 100);
    }, 200);
  };

  // Add gentle idle animation
  useEffect(() => {
    let startTime;

    const animateIdle = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;

      // Only apply idle animation when not actively interacted with
      if (!isHovered && !isExternalHover) {
        // Create a gentle sine wave movement
        const xMovement =
          Math.sin(elapsedTime * 0.001) * IDLE_MOVEMENT_AMPLITUDE;
        const yMovement =
          Math.cos(elapsedTime * 0.0015) * IDLE_MOVEMENT_AMPLITUDE;

        targetRotation.current = {
          x: yMovement,
          y: xMovement,
          z: 0,
          translateX: xMovement * 2,
          translateY: yMovement * 2,
        };
      }

      animationFrameRef.current = requestAnimationFrame(animateIdle);
    };

    animationFrameRef.current = requestAnimationFrame(animateIdle);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isHovered, isExternalHover]);

  // Setup improved element-specific hover detection
  useEffect(() => {
    // Custom contains selector for jQuery-like functionality
    const getElementsContainingText = (text) => {
      const elements = document.querySelectorAll("h3, .flex");
      return Array.from(elements).filter((el) =>
        el.textContent.toLowerCase().includes(text.toLowerCase())
      );
    };

    // Get all target elements
    const napisteElements = getElementsContainingText("napíšte");
    const zavolajteElements = getElementsContainingText("zavolajte");
    const vyplnteElements = getElementsContainingText("vyplňte");

    // Handle mouse enter for specific elements
    const handleNapisteEnter = () => {
      // If the astronaut was recently directly hovered, clear that state
      if (wasHovered.current) {
        // Reset the direct hover influence
        wasHovered.current = false;
        setIsHovered(false);
      }

      prevExternalTarget.current = externalTarget;
      setIsExternalHover(true);
      setExternalTarget("napiste");
    };

    const handleZavolajteEnter = () => {
      // If the astronaut was recently directly hovered, clear that state
      if (wasHovered.current) {
        // Reset the direct hover influence
        wasHovered.current = false;
        setIsHovered(false);
      }

      prevExternalTarget.current = externalTarget;
      setIsExternalHover(true);
      setExternalTarget("zavolajte");
    };

    const handleVyplnteEnter = () => {
      // If the astronaut was recently directly hovered, clear that state
      if (wasHovered.current) {
        // Reset the direct hover influence
        wasHovered.current = false;
        setIsHovered(false);
      }

      prevExternalTarget.current = externalTarget;
      setIsExternalHover(true);
      setExternalTarget("vyplnte");
    };

    // Handle mouse leave for all elements
    const handleElementLeave = () => {
      prevExternalTarget.current = externalTarget;
      setIsExternalHover(false);
      setExternalTarget(null);
    };

    // Add event listeners to all found elements
    napisteElements.forEach((el) => {
      el.addEventListener("mouseenter", handleNapisteEnter);
      el.addEventListener("mouseleave", handleElementLeave);
    });

    zavolajteElements.forEach((el) => {
      el.addEventListener("mouseenter", handleZavolajteEnter);
      el.addEventListener("mouseleave", handleElementLeave);
    });

    vyplnteElements.forEach((el) => {
      el.addEventListener("mouseenter", handleVyplnteEnter);
      el.addEventListener("mouseleave", handleElementLeave);
    });

    // Clean up
    return () => {
      napisteElements.forEach((el) => {
        el.removeEventListener("mouseenter", handleNapisteEnter);
        el.removeEventListener("mouseleave", handleElementLeave);
      });

      zavolajteElements.forEach((el) => {
        el.removeEventListener("mouseenter", handleZavolajteEnter);
        el.removeEventListener("mouseleave", handleElementLeave);
      });

      vyplnteElements.forEach((el) => {
        el.removeEventListener("mouseenter", handleVyplnteEnter);
        el.removeEventListener("mouseleave", handleElementLeave);
      });

      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }

      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, [externalTarget]);

  // Determine rotation based on external target
  const getExternalTargetRotation = () => {
    if (!externalTarget) return { x: 0, y: 0, z: 0 };

    // Add small oscillations for continuous movement
    const oscillationX = Math.sin(Date.now() * 0.001) * 0.8;
    const oscillationY = Math.cos(Date.now() * 0.0015) * 0.8;
    const oscillationZ = Math.sin(Date.now() * 0.0012) * 0.5;

    // Default rotations for each section - with small oscillations
    switch (externalTarget) {
      case "napiste":
        return {
          x: -2 + oscillationX,
          y: 10 + oscillationY,
          z: -10 + oscillationZ,
        };
      case "zavolajte":
        return {
          x: oscillationX,
          y: 12 + oscillationY,
          z: oscillationZ,
        };
      case "vyplnte":
        return {
          x: 20 + oscillationX,
          y: oscillationY,
          z: 30 + oscillationZ,
        };
      default:
        return { x: 0, y: 0, z: 0 };
    }
  };

  // Calculate distance between two rotation states to determine animation speed
  const getAnimationDistance = (targetRot, currentRot) => {
    return Math.sqrt(
      Math.pow(targetRot.x - currentRot.x, 2) +
        Math.pow(targetRot.y - currentRot.y, 2) +
        Math.pow(targetRot.z - currentRot.z, 2)
    );
  };

  // Animate the rotation smoothly
  useEffect(() => {
    // Update target rotation based on current state
    const updateTargetRotation = () => {
      if (isHovered) {
        // Direct hover on image - use mouse position
        targetRotation.current = {
          x: mousePosition.y * 10,
          y: -mousePosition.x * 10,
          z: 0,
          translateX: mousePosition.x * 10,
          translateY: mousePosition.y * 10,
        };
      } else if (isExternalHover) {
        // External hover - use predefined values with subtle animation
        const externalRotation = getExternalTargetRotation();
        targetRotation.current = {
          x: externalRotation.x,
          y: externalRotation.y,
          z: externalRotation.z,
          translateX: externalRotation.y * 0.3,
          translateY: externalRotation.x * 0.3,
        };
      }
      // If neither, the idle animation will take over
    };

    // Smoothly animate to target rotation
    const animateRotation = () => {
      updateTargetRotation();

      // Calculate current animation speed based on context
      let currentSpeed = ANIMATION_SPEED.default;

      // Determine appropriate animation speed
      if (isHovered) {
        // Direct mouse hover - faster response
        currentSpeed = ANIMATION_SPEED.mouseHover;
      } else if (isExternalHover) {
        // Moving to external target - slower for bigger movements
        currentSpeed = ANIMATION_SPEED.externalHover;
      } else if (prevExternalTarget.current) {
        // Just left an external target - use exit speed
        currentSpeed = ANIMATION_SPEED.externalExit;
      }

      // Calculate distance to adjust speed for larger movements
      const distance = getAnimationDistance(
        targetRotation.current,
        currentRotation.current
      );

      if (distance > 15) {
        // For very large movements, slow down even more
        currentSpeed *= 0.8;
      }

      // Smoothly interpolate between current and target rotation
      currentRotation.current = {
        x:
          currentRotation.current.x +
          (targetRotation.current.x - currentRotation.current.x) * currentSpeed,
        y:
          currentRotation.current.y +
          (targetRotation.current.y - currentRotation.current.y) * currentSpeed,
        z:
          currentRotation.current.z +
          (targetRotation.current.z - currentRotation.current.z) * currentSpeed,
        translateX:
          currentRotation.current.translateX +
          (targetRotation.current.translateX -
            currentRotation.current.translateX) *
            currentSpeed,
        translateY:
          currentRotation.current.translateY +
          (targetRotation.current.translateY -
            currentRotation.current.translateY) *
            currentSpeed,
      };

      // If we're very close to target and not externally hovering,
      // clear the previous target to allow normal speed again
      if (!isExternalHover && !isHovered && distance < 1) {
        prevExternalTarget.current = null;
      }

      // Force a re-render to apply the new rotation
      forceUpdate();

      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(animateRotation);
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animateRotation);

    // Clean up animation on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    isHovered,
    isExternalHover,
    mousePosition.x,
    mousePosition.y,
    externalTarget,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Force component to update without state change
  const [, updateState] = useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  return (
    <div
      className={`w-full mb-6 bg-lightgray flex justify-center md:hidden ${className}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className='rounded-xl relative overflow-hidden lg:mr-8 transition-all duration-300'>
        {/* Background image - static */}
        <img
          src={backgroundSrc}
          alt='Space background'
          className={`w-full ${imageHeight} object-cover`}
          loading='lazy'
        />

        {/* Astronaut image - this is the one that rotates */}
        <img
          src={astronautSrc}
          alt={alt}
          className={`absolute top-0 left-0 w-full h-full object-contain transition-filter duration-500 ${
            blurEffect ? "blur-xs" : ""
          }`}
          loading='lazy'
          style={{
            transform: `perspective(1000px) rotateX(${currentRotation.current.x}deg) rotateY(${currentRotation.current.y}deg) rotateZ(${currentRotation.current.z}deg) scale(${isHovered ? 1.05 : 1}) translateX(${currentRotation.current.translateX}px) translateY(${currentRotation.current.translateY}px)`,
            // Remove transition from transform to allow smooth animation frame updates
            transition: "filter 0.2s ease-out",
          }}
        />
      </div>
    </div>
  );
};

export default AstronautImage;
